import { UserOutlined } from '@ant-design/icons';
import type { PersonaGiuridicaDTO } from '@framework/api-client';
import {
  AuthActions,
  getNetworkErrorMessage,
  isResponse401,
  useCurrentUser,
  useLogout,
} from '@framework/auth';
import { t } from '@framework/i18n';
import {
  LoaderSpin,
  LoginCard,
  useBreadcrumbItem,
  VerticalSpace,
} from '@framework/ui';
import { Button, Flex, message } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LegalEntityChoiceCard } from './LegalEntityChoiceCard';
import {
  getLegalEntityInitials,
  getLegalEntityTypeLabel,
} from './legalEntityDisplay';
import { useLegalEntityIdentityActions } from './useLegalEntityIdentityActions';
import { useLegalEntitySelection } from './useLegalEntitySelection';

/**
 * Schermata di scelta successiva al login PF (SPID/CIE/CNS) che permette
 * all'utente di proseguire come persona fisica oppure per conto di una
 * persona giuridica associata.
 */
export function FrontLoginSelectLegalEntityForm() {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const { logout } = useLogout();
  const { usableLegalEntities } = useLegalEntitySelection();
  const { resetProfileSelection, switchToSelf, switchToLegalEntity } =
    useLegalEntityIdentityActions();

  const [pendingId, setPendingId] = useState<string | null>(null);
  const isSwitchingIdentityRef = useRef(false);
  const sortedLegalEntities = useMemo(
    () => sortLegalEntities(usableLegalEntities),
    [usableLegalEntities],
  );

  const user = currentUser?.utenteLoggato;
  const isSwitchingIdentity = pendingId != null;

  useBreadcrumbItem({
    title: t('user.front-login.select-pg.breadcrumb', 'Accedi come'),
    menuKey: 'login',
  });

  const proceedAsSelf = useCallback(async () => {
    if (isSwitchingIdentityRef.current) return;

    isSwitchingIdentityRef.current = true;
    setPendingId('self');
    try {
      await switchToSelf();
    } catch (e: any) {
      isSwitchingIdentityRef.current = false;
      setPendingId(null);
      handleIdentitySwitchError(e, dispatch, resetProfileSelection);
    }
  }, [switchToSelf, dispatch, resetProfileSelection]);

  const proceedAsLegalEntity = useCallback(
    async (entity: PersonaGiuridicaDTO) => {
      if (isSwitchingIdentityRef.current || !entity.id) return;

      isSwitchingIdentityRef.current = true;
      setPendingId(entity.id);
      try {
        await switchToLegalEntity(entity);
      } catch (e: any) {
        isSwitchingIdentityRef.current = false;
        setPendingId(null);
        handleIdentitySwitchError(e, dispatch, resetProfileSelection);
      }
    },
    [switchToLegalEntity, dispatch, resetProfileSelection],
  );

  return (
    <LoaderSpin spinning={!user} transparent>
      <LoginCard
        srOnlyText={t(
          'user.front-login.select-pg.page-title',
          'Scegli con quale identità accedere.',
        )}
        title={t('user.front-login.select-pg.title', 'Accedi come')}
        subTitle={t(
          'user.front-login.select-pg.subtitle',
          'Seleziona il profilo con cui desideri operare',
        )}
        fullWidth
      >
        <LegalEntityChoiceCard
          avatar={<UserOutlined />}
          title={t('user.front-login.select-pg.self-option', 'Me stesso')}
          subtitle={t(
            'user.front-login.select-pg.self-subtitle',
            'Accedi come persona fisica',
          )}
          loading={pendingId === 'self'}
          disabled={isSwitchingIdentity}
          onSelect={proceedAsSelf}
        />

        {sortedLegalEntities.map(entity => (
          <LegalEntityChoiceCard
            key={entity.id}
            avatar={getLegalEntityInitials(entity.ragioneSociale)}
            title={t(
              'user.front-login.select-pg.act-as',
              'Operare per conto di {{name}}',
              { name: entity.ragioneSociale ?? '' },
            )}
            subtitle={buildLegalEntitySubtitle(entity)}
            loading={pendingId === entity.id}
            disabled={isSwitchingIdentity}
            onSelect={() => proceedAsLegalEntity(entity)}
          />
        ))}

        <VerticalSpace size={24} />
        <Flex justify="center">
          <Button
            type="link"
            disabled={isSwitchingIdentity}
            onClick={() => logout?.()}
          >
            {t('user.front-login.select-pg.back-to-login', 'Torna al login')}
          </Button>
        </Flex>
      </LoginCard>
    </LoaderSpin>
  );
}

function handleIdentitySwitchError(
  e: any,
  dispatch: ReturnType<typeof useDispatch>,
  resetProfileSelection: () => void,
) {
  if (isResponse401(e?.response)) {
    resetProfileSelection();
    dispatch(AuthActions.logout({ isRefreshTokenExpired: true }));
    message.error(
      t(
        'user.front-login.select-pg.session-expired',
        'Sessione scaduta. Effettuare nuovamente il login.',
      ),
    );
    return;
  }

  message.error(
    getNetworkErrorMessage(
      e,
      t(
        'user.front-login.select-pg.generic-error',
        'Si è verificato un errore. Riprovare.',
      ),
    ),
  );
}

function sortLegalEntities(entities: PersonaGiuridicaDTO[]) {
  return [...entities].sort((a, b) =>
    (a.ragioneSociale ?? '').localeCompare(b.ragioneSociale ?? ''),
  );
}

function buildLegalEntitySubtitle(entity: PersonaGiuridicaDTO) {
  const parts: string[] = [];
  if (entity.partitaIva) {
    parts.push(
      `${t('user.front-login.select-pg.vat-number', 'P.IVA:')} ${
        entity.partitaIva
      }`,
    );
  }
  const typeLabel = getLegalEntityTypeLabel(entity.tipoPg);
  if (typeLabel) parts.push(typeLabel);
  return parts.join(' · ');
}
